import { Badge, Button, Field, Input } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ICONS, glyph } from '../components/ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useAppToast } from '../hooks/useAppToast';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { SkeletonText } from '../components/ui/Skeleton';

interface Organization {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  planStatus: string;
  trialEndsAt?: string;
  createdAt: string;
  plan: { id: string; name: string; displayName: string } | null;
  quota: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
  };
  usage: {
    users: number;
    contacts: number;
    campaigns: number;
    connections: number;
  };
}

interface Member {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  criadoEm: string;
}

function QuotaCard({
  label,
  used,
  max,
  icon,
}: {
  label: string;
  used: number;
  max: number;
  icon: string;
}) {
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const barColor = pct >= 80 ? 'var(--crm-text-error)' : pct >= 60 ? 'var(--crm-text-warning)' : 'var(--brand-text)';
  const textColor = pct >= 80 ? 'var(--crm-text-error)' : pct >= 60 ? 'var(--crm-text-warning)' : 'var(--text-muted)';

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '20px 24px',
      flex: '1 1 140px',
      minWidth: 0,
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'var(--surface-2)', border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {glyph(icon, 16)}
        </div>
        <div className="font-semibold" style={{ fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 10 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{used}</span>
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>/ {max}</span>
      </div>

      <div style={{ background: 'var(--border)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{
          background: barColor,
          width: `${pct}%`,
          height: '100%',
          borderRadius: 4,
          transition: 'width 0.4s ease',
        }} />
      </div>
      <div style={{ fontSize: 11, color: textColor, marginTop: 4 }}>
        {pct.toFixed(0)}% utilizado
      </div>
    </div>
  );
}

function roleBadge(role: string) {
  const map: Record<string, { label: string; color: string }> = {
    SUPERADMIN: { label: 'Super Admin', color: '#6C47FF' },
    ADMIN: { label: 'Admin', color: 'var(--brand-text)' },
    TENANT_ADMIN: { label: 'Tenant Admin', color: 'var(--link)' },
    USER: { label: 'Usuário', color: 'var(--text-muted)' },
  };
  const entry = map[role] || { label: role, color: 'var(--text-muted)' };
  return (
    <span style={{
      background: entry.color + '15',
      color: entry.color,
      border: `1px solid ${entry.color}30`,
      borderRadius: 12,
      padding: '2px 10px',
      fontSize: 11,
      fontWeight: 600,
    }}>
      {entry.label}
    </span>
  );
}

function planStatusInfo(status: string, trialEndsAt?: string) {
  const daysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000))
    : null;

  const map: Record<string, { label: string; color: 'blue' | 'green' | 'orange' | 'red' }> = {
    TRIAL: { label: 'Trial', color: 'blue' },
    ACTIVE: { label: 'Ativo', color: 'green' },
    SUSPENDED: { label: 'Suspenso', color: 'orange' },
    CANCELLED: { label: 'Cancelado', color: 'red' },
  };
  return { ...map[status] || { label: status, color: 'blue' as const }, daysLeft };
}

export function OrganizacaoPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const toast = useAppToast();

  const [org, setOrg] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdmin = ['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '');
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Helper autenticado (canónico, inclui X-Tenant-Id automaticamente)
  const authenticatedFetch = useAuthenticatedFetch();

  const load = async () => {
    setLoading(true);
    try {
      const [orgRes, membersRes] = await Promise.all([
        authenticatedFetch('/api/tenants/my-organization'),
        authenticatedFetch('/api/users?limit=100'),
      ]);

      if (orgRes.ok) {
        const data = await orgRes.json();
        setOrg(data.organization);
        setEditName(data.organization?.name || '');
      }

      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.users || []);
      }
    } catch {
      toast.error('Erro ao carregar dados da organização');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [selectedTenantId]);

  const handleSave = async () => {
    if (!org || !editName.trim()) return;
    setSaving(true);
    try {
      const res = await authenticatedFetch(`/api/tenants/${org.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (res.ok) {
        toast.success('Nome da organização atualizado');
        setEditOpen(false);
        await load();
      } else {
        const err = await res.json();
        toast.error(err.message || 'Erro ao salvar');
      }
    } catch {
      toast.error('Erro ao salvar organização');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div style={{ padding: '28px 40px', background: 'var(--surface-2)', minHeight: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
          <SkeletonText lines={5} label="Carregando organização" />
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div style={{ padding: '28px 40px', background: 'var(--surface-2)', minHeight: '100%' }}>
        <div style={{ color: 'var(--text-muted)' }}>Organização não encontrada.</div>
      </div>
    );
  }

  const planInfo = planStatusInfo(org.planStatus, org.trialEndsAt);

  if (editOpen) {
    return (
      <FormPage
        backTo="/organizacao"
        backLabel="Organização"
        onBack={() => setEditOpen(false)}
        title={'Editar organização'}
        description="Nome e dados da organização."
        submitLabel={'Salvar'}
        submitting={saving}
        submitDisabled={!editName.trim() || saving}
        onSubmit={handleSave}
        onCancel={() => setEditOpen(false)}
      >
        <FormSection title="Detalhes">


        <div style={{ padding: '4px 0 8px' }}>
          <div style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 13 }}>
            Atualize as informações básicas da organização.
          </div>

          <div style={{ marginBottom: 24 }}>
            <Field label="Nome da organização">
              <Input onChange={(e) => setEditName(e.target.value)} value={editName} />
            </Field>
          </div>

          </div>
        </FormSection>
      </FormPage>
    );
  }

  return (
    <div style={{ padding: '28px 40px', background: 'var(--surface-2)', minHeight: '100%' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Organização</h2>
          <div style={{ color: 'var(--text-muted)' }}>Visão geral, quotas e membros da sua organização</div>
        </div>
        {isSuperAdmin && (
          <Button variant="secondary" icon={ICONS.Settings} onClick={() => navigate('/super-admin')}>
            Gerenciar todas
          </Button>
        )}
      </div>

      {/* Org identity card */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '28px 32px',
        marginBottom: 24,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20 }}>
          {/* Logo circle */}
          <div style={{
            width: 56, height: 56, borderRadius: 12,
            background: 'var(--brand-text)', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: 'var(--surface)', fontSize: 22, fontWeight: 700 }}>
              {org.name.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* Info */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h3 className="text-lg font-semibold" style={{ fontSize: 20, margin: 0 }}>{org.name}</h3>
              <Badge variant={org.active ? 'green' : 'red'}>{org.active ? 'Ativa' : 'Inativa'}</Badge>
              <Badge variant={planInfo.color}>{planInfo.label}</Badge>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                <span style={{ color: 'var(--text)', fontFamily: 'monospace' }}>/{org.slug}</span>
              </div>
              {org.plan && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Plano: <strong>{org.plan.displayName || org.plan.name}</strong>
                </div>
              )}
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                Criada em {formatDate(org.createdAt)}
              </div>
            </div>

            {org.planStatus === 'TRIAL' && org.trialEndsAt && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: planInfo.daysLeft !== null && planInfo.daysLeft <= 3 ? 'var(--crm-bg-warning)' : 'var(--tint-blue)',
                border: `1px solid ${planInfo.daysLeft !== null && planInfo.daysLeft <= 3 ? 'var(--crm-text-warning)' : 'var(--link)'}`,
                borderRadius: 6, padding: '5px 12px', marginTop: 10,
              }}>
                <ICONS.InfoWithCircle size={16} />
                <div style={{ fontSize: 12, color: planInfo.daysLeft !== null && planInfo.daysLeft <= 3 ? 'var(--crm-text-warning)' : 'var(--link)' }}>
                  {planInfo.daysLeft !== null && planInfo.daysLeft > 0
                    ? `Trial expira em ${planInfo.daysLeft} dia${planInfo.daysLeft !== 1 ? 's' : ''} — ${formatDate(org.trialEndsAt)}`
                    : 'Trial expirado'}
                </div>
              </div>
            )}
          </div>

          {/* Edit button (admin+) */}
          {isAdmin && (
            <Button variant="secondary" icon={ICONS.Edit} onClick={() => { setEditName(org.name); setEditOpen(true); }}>
              Editar
            </Button>
          )}
        </div>
      </div>

      {/* Quota cards */}
      <div style={{ marginBottom: 8 }}>
        <div className="font-semibold" style={{ color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
          Uso e Quotas
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <QuotaCard label="Usuários" used={org.usage.users} max={org.quota.maxUsers} icon="InviteUser" />
          <QuotaCard label="Contatos" used={org.usage.contacts} max={org.quota.maxContacts} icon="Person" />
          <QuotaCard label="Campanhas interativas" used={org.usage.campaigns} max={org.quota.maxCampaigns} icon="Megaphone" />
          <QuotaCard label="Conexões" used={org.usage.connections} max={org.quota.maxConnections} icon="Copy" />
        </div>
      </div>

      {/* Members section */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="font-semibold" style={{ color: 'var(--text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Membros da Organização
          </div>
          {isAdmin && (
            <Button variant="secondary" icon={ICONS.InviteUser} onClick={() => navigate('/usuarios')}>
              Gerenciar membros
            </Button>
          )}
        </div>

        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 120px 100px',
            padding: '10px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
          }}>
            {['Nome', 'Email', 'Função', 'Status'].map(col => (
              <div className="font-semibold lg-th" key={col}>
                {col}
              </div>
            ))}
          </div>

          {members.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <div style={{ color: 'var(--text-muted)' }}>Nenhum membro encontrado</div>
            </div>
          ) : (
            members.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 120px 100px',
                  padding: '12px 20px',
                  borderBottom: i < members.length - 1 ? '1px solid var(--border)' : 'none',
                  alignItems: 'center',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: '#00684A20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <span style={{ color: 'var(--brand-text)', fontWeight: 700, fontSize: 13 }}>
                      {m.nome.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="font-semibold" style={{ fontSize: 13 }}>{m.nome}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{m.email}</div>
                <div>{roleBadge(m.role)}</div>
                <div>
                  <span style={{
                    background: m.ativo ? 'var(--tint-green)' : 'var(--tint-red)',
                    color: m.ativo ? 'var(--brand-text)' : 'var(--crm-text-error)',
                    border: `1px solid ${m.ativo ? '#00684A30' : '#E3252F30'}`,
                    borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600,
                  }}>
                    {m.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit modal */}

    </div>
  );
}
